import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { firebaseService } from '@/lib/firebase';
import { Cloud, Database } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = React.useState(false);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const user = await firebaseService.signInWithGoogle();
      if (user) {
        // Mark that user has made a choice
        localStorage.setItem('star-chore-onboarding-complete', 'true');
        toast({
          title: 'Welcome!',
          description: 'Successfully signed in with Google.',
        });
        navigate('/');
      } else {
        toast({
          title: 'Sign In Failed',
          description: 'Could not sign in with Google. Please try again.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error signing in:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while signing in.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLocalStorage = () => {
    // Mark that user has made a choice
    localStorage.setItem('star-chore-onboarding-complete', 'true');
    toast({
      title: 'Welcome!',
      description: 'You can sign in anytime from the Admin tab in Settings.',
    });
    navigate('/');
  };

  return (
    <div className="min-h-[100dvh] w-full flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 dark:from-purple-950 dark:via-pink-950 dark:to-blue-950 p-4 pt-[calc(1rem+env(safe-area-inset-top,0px))] pb-[calc(1rem+env(safe-area-inset-bottom,0px))]">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">⭐</div>
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Star Chore Adventure
          </h1>
          <p className="text-lg text-muted-foreground">
            Turn chores into an adventure! Earn stars, unlock rewards, and have fun.
          </p>
        </div>

        {/* Options */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Google Sign In Option */}
          <Card className="relative overflow-hidden border-2 hover:border-primary/50 transition-all">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Cloud className="h-8 w-8 text-primary" />
              </div>
              <CardTitle className="text-2xl">Sign in with Google</CardTitle>
              <CardDescription className="text-base">
                Sync your data across all devices
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Automatic cloud backup</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Access your data from any device</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Never lose your progress</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Sync tasks, rewards, and achievements</span>
                </li>
              </ul>
              <Button
                className="w-full"
                size="lg"
                onClick={handleGoogleSignIn}
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign in with Google'}
              </Button>
            </CardContent>
          </Card>

          {/* Local Storage Option */}
          <Card className="relative overflow-hidden border-2 hover:border-primary/50 transition-all">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Database className="h-8 w-8 text-blue-500" />
              </div>
              <CardTitle className="text-2xl">Use Local Storage</CardTitle>
              <CardDescription className="text-base">
                Store data on this device only
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>No account required</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Complete privacy</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Works offline</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✓</span>
                  <span>Sign in later from Settings → Admin</span>
                </li>
              </ul>
              <Button
                className="w-full"
                variant="outline"
                size="lg"
                onClick={handleLocalStorage}
              >
                Continue with Local Storage
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            You can change your choice anytime from the <strong>Admin tab</strong> in Settings
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
